import { createSelector } from '@reduxjs/toolkit';

import {
  DEFAULT_PROJECT_PROPERTY_TYPE,
  ProjectPropertyType,
} from 'src/constants/project';
import { diagramSnapshotSelector } from 'src/selectors/diagramSnapshot';
import { memoizeSelectorFactory } from 'src/selectors/memoizeSelectorFactory';
import { projectSelector } from 'src/selectors/project';

export const projectPropertyTypeSelector = memoizeSelectorFactory(
  (projectId: string) =>
    createSelector(
      projectSelector(projectId),
      (project) => project?.propertyType || DEFAULT_PROJECT_PROPERTY_TYPE
    )
);

export const isCustomPropertyTypeSelector = memoizeSelectorFactory(
  (projectId: string) =>
    createSelector(
      projectPropertyTypeSelector(projectId),
      (projectPropertyType) =>
        projectPropertyType === ProjectPropertyType.CUSTOM
    )
);

export const propertyTypesByIriSelector = memoizeSelectorFactory(
  (projectId: string) =>
    createSelector(
      diagramSnapshotSelector(projectId),
      (diagram) => diagram?.model?.modelData?.propertyTypesByIri || {}
    )
);

export const propertyTypeByIriSelector = memoizeSelectorFactory(
  (projectId: string, iri?: string) =>
    createSelector(
      isCustomPropertyTypeSelector(projectId),
      projectPropertyTypeSelector(projectId),
      propertyTypesByIriSelector(projectId),
      (isCustomPropertyType, projectPropertyType, propertyTypesByIri) => {
        const customPropertyType = iri
          ? propertyTypesByIri[iri]
          : DEFAULT_PROJECT_PROPERTY_TYPE;

        return {
          isCustomPropertyType,
          propertyType: isCustomPropertyType
            ? customPropertyType
            : projectPropertyType,
        };
      }
    )
);

export const propertyTypeCountSelector = memoizeSelectorFactory(
  (projectId: string) =>
    createSelector(
      propertyTypesByIriSelector(projectId),
      (propertyTypesByIri) => {
        const propertyTypeCount = {
          [ProjectPropertyType.RDFS]: 0,
          [ProjectPropertyType.SCHEMA_ORG]: 0,
          total: 0,
        };
        Object.entries(propertyTypesByIri).forEach(([_, propertyType]) => {
          if (propertyType !== ProjectPropertyType.CUSTOM) {
            propertyTypeCount[propertyType] += 1;
            propertyTypeCount.total += 1;
          }
        });
        return {
          isAllPropertyType: {
            [ProjectPropertyType.RDFS]:
              propertyTypeCount[ProjectPropertyType.RDFS] >=
              propertyTypeCount.total,
            [ProjectPropertyType.SCHEMA_ORG]:
              propertyTypeCount[ProjectPropertyType.SCHEMA_ORG] >=
              propertyTypeCount.total,
          },
          propertyTypeCount,
        };
      }
    )
);
